import { DEVELOPMENT } from "@/config/config";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logout as apiLogout } from "../api/auth.api";
import { User } from "@/types";

// Mock user for development
const mockUser: User = {
  id: "1",
  username: "demo_user",
  firstName: '',
  lastName: '',
  email: "demo_user@gmail.com",
  avatar: '',
  verified: true, // Retained field flag
  joinDate: new Date("2026-07-27T19:20:45.920Z"),
  ideas: 0,
  views: 0,
  followers: 0,
  following: 1, // Mapped from subscribed array length
  bio: "Innovation enthusiast and idea creator. Passionate about turning creative concepts into reality through technology and collaboration.",
  banner: 'demo_user',
  dateJoined: "2026-07-27T19:20:45.920Z",
  totalViews: 0,
  subscribers: 0,
  displayName: "demo_user",
  planType: "pro_monthly:plm1",
  monthlyUsage: {
    ideaPosts: 1,
    aiCredits: -1,
    month: "2026-07",
  },
};

export interface AppState {
  user: User | null;
  isLoggedIn: boolean;
  token: string | null;
  hasSeenTutorial: boolean;
  runTutorial: boolean;
  setUser: (user: User | null) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setToken: (token: string | null) => void;
  setHasSeenTutorial: (hasSeen: boolean) => void;
  setRunTutorial: (run: boolean) => void;
  logout: () => Promise<void>;
  isBlocked: boolean;
  setIsBlocked: (blocked: boolean) => void;
  checkAuthStatus: (session: any) => boolean;
  initializeMockUser: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoggedIn: false,
      isBlocked: false,
      token: null,
      hasSeenTutorial: false,
      runTutorial: false,
      setUser: (user) => set({ user }),
      setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn }),
      setToken: (token) => set({ token }),
      setIsBlocked: (isBlocked) => set({ isBlocked }),
      setHasSeenTutorial: (hasSeen) => set({ hasSeenTutorial: hasSeen }),
      setRunTutorial: (run) => set({ runTutorial: run }),
      logout: async () => {
        try {
          await apiLogout();
        } catch (e) {
          console.error("Logout API call failed:", e);
        }
        localStorage.removeItem("jwt");
        localStorage.removeItem("refreshToken");
        set({ user: null, isLoggedIn: false, token: null });
      },
      checkAuthStatus: (session) => {
        const token = localStorage.getItem("jwt");
        const refreshToken = localStorage.getItem("refreshToken");
        const hasValidTokens = !!(token && refreshToken);

        if (hasValidTokens && !get().isLoggedIn) {
          const userData = session ? session() : null;
          set({ isLoggedIn: true, token, user: userData });
        }
        // With HttpOnly cookies, localStorage won't have tokens, so we MUST NOT
        // aggressively log the user out here just because hasValidTokens is false.
        // We will rely on apiClient.ts returning 401s to handle real session expiry.

        return get().isLoggedIn;
      },
      initializeMockUser: () => {
        // Only initialize mock user in development
        if (
          process.env.NODE_ENV === "development" &&
          DEVELOPMENT &&
          !get().user
        ) {
          set({ user: mockUser, isLoggedIn: true, token: "mock-token" });
        }
      },
    }),
    {
      name: "idearify-app-storage",
      partialize: (state) => ({
        user: state.user,
        isLoggedIn: state.isLoggedIn,
        token: state.token,
        hasSeenTutorial: state.hasSeenTutorial,
      }),
    }
  )
);
